import { FieldValues } from 'react-hook-form';
import { GENDER } from 'commerce/scayle/constants';
import { BapiProduct } from 'commerce/scayle/user-interaction/types';

export type AllFormFieldTypes =
  | ICustomerForm
  | LoginCredentials
  | UpdatePassword
  | PasswordType;

export interface IAddressCollectionPoint {
  description?: string;
  customerKey?: string;
  key: string;
  type: string;
}

export interface IAddressForm {
  gender?: GENDER;
  firstName: string;
  lastName: string;
  zip: string;
  city: string;
  street: string;
  street2?: string;
  company?: string;
  isDefault?: {
    billing: boolean;
    shipping: boolean;
  };
  tax?: string;
  collectionPoint?: IAddressCollectionPoint;
  email?: string;
}

export interface IProfileForm extends IAddressForm {
  email: string;
  password?: string;
  company?: string;
  tax?: string;
  newPassword?: string;
  addressId?: number;
  shouldUpdatePersonalData?: boolean;
}

export interface ICustomerForm extends IProfileForm {
  newsletter?: boolean;
  password?: string;
}

export interface ICheckoutGuest extends ICustomerForm {
  sameShipping?: boolean | undefined;
  shipping?: IAddressForm;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  gender: GENDER;
  orderSummary?: {
    id: string;
  }[];
  status: {
    isGuestCustomer: boolean;
  };
  customData?: {
    company?: {
      name: string;
      'tax-number': string;
    };
    senderId?: string;
  };
}

export interface OrderItem {
  createdAt: string;
  currency: string;
  id: number;
  isManuallyReturnedByCci: false;
  key: string;
  merchant: { id: number };
  packageId: number;
  price: {
    overrideWithTax: number;
    overrideWithoutTax: number;
    tax: { vat: { amount: number; rate: number } };
    undiscountedWithOutTax: number;
    undiscountedWithTax: number;
    withTax: number;
    withoutTax: number;
  };
  product: BapiProduct & {
    name: string;
  };
  status: string;
  updatedAt: string;
  variant: {
    createdAt: string;
    id: number;
    referenceKey: string;
    stock: {
      isSellableWithoutStock: false;
      quantity: number;
      supplierId: number;
      warehouseId: number;
    };
    updatedAt: string;
  };
  warehouseId: number;
}

export interface IOrder {
  id: number;
  createdAt: string;
  status: string;
  items: OrderItem[];
  detailedStatus: {
    order: {
      name: string;
    };
  };
  payment: {
    key: string;
    transactionKey: string;
  }[];
  address: {
    billing: Address;
    shipping: Address;
  };
  customer: {
    email: string;
    firstName: string;
    lastName: string;
    status: {
      isActive: boolean;
      isGuestCustomer: boolean;
    };
    gender: GENDER.MALE | GENDER.FEMALE | GENDER.COMPANY;
  };
  cost: {
    appliedFees?: {
      amount: { withTax: number; withoutTax: number };
      category: string;
    }[];
    tax: { vat: { amount: number } };
    withTax: number;
    withoutTax: number;
  };
  currencyCode: string;
  vauchers?: { code: string }[];
}

export interface Address {
  city: string;
  countryCode: string;
  createdAt: string;
  houseNumber: string;
  id: number;
  isBillingAddress: boolean;
  isDefault: {
    billing: boolean;
    shipping: boolean;
  };
  isShippingAddress: boolean;
  recipient: Recipient;
  street: string;
  additional?: string;
  updatedAt: string;
  zipCode: string;
}

interface Recipient {
  firstName: string;
  gender: GENDER;
  lastName: string;
  title: string;
  type: 'personal' | 'business';
}

export interface LoginCredentials extends FieldValues {
  email: string;
  password: string;
}

export interface UpdatePassword extends FieldValues {
  password: string;
}

export interface PasswordType {
  email: string;
}

export type AuthResponse = {
  token_type: string;
  expires_in: number;
  access_token: string;
  refresh_token: string;
};

/*
 * /api/auth/{:path} will be redirect to process.env.NEXT_PUBLIC_SCAYLE_AUTH_API_ENDPOINT/auth/:path
 * Authentication API docs https://scayle.dev/en/dev/authentication/introduction
 * */
export enum AuthApiUrls {
  BASE_AUTH_API = '/api/auth',

  LOGIN = `${BASE_AUTH_API}/login`,
  LOGOUT = `${BASE_AUTH_API}/logout`,
  REGISTER = `${BASE_AUTH_API}/register`,
  RESET_PASSWORD = `${BASE_AUTH_API}/password/send-reset-email`,
  UPDATE_PASSWORD = `${BASE_AUTH_API}/password/update-by-hash`,
  LOGIN_GUEST = `${BASE_AUTH_API}/login/guest`,
}
